import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  CheckCircle2, 
  Package, 
  Truck, 
  Clock, 
  X, 
  DollarSign,
  AlertTriangle,
  ArrowRight,
  Loader
} from 'lucide-react';
import { format } from 'date-fns';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import api from '@/utils/api';
import type { Order } from '@/types';

interface OrderWorkflowProps {
  order: Order;
  onUpdate?: () => void;
}

const statusConfig = {
  pending: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
    nextStatuses: ['confirmed', 'cancelled'],
    description: 'Order has been placed and is awaiting confirmation'
  },
  confirmed: {
    label: 'Confirmed',
    color: 'bg-blue-100 text-blue-800',
    icon: CheckCircle2,
    nextStatuses: ['processing', 'cancelled'],
    description: 'Order has been confirmed and payment verified'
  },
  processing: {
    label: 'Processing',
    color: 'bg-purple-100 text-purple-800',
    icon: Package,
    nextStatuses: ['shipped', 'cancelled'],
    description: 'Order is being prepared for shipment'
  },
  shipped: {
    label: 'Shipped',
    color: 'bg-indigo-100 text-indigo-800',
    icon: Truck,
    nextStatuses: ['delivered'],
    description: 'Order has been shipped and is on the way'
  },
  delivered: {
    label: 'Delivered',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle2,
    nextStatuses: [],
    description: 'Order has been successfully delivered'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-800',
    icon: X,
    nextStatuses: [],
    description: 'Order has been cancelled'
  },
  refunded: {
    label: 'Refunded',
    color: 'bg-gray-100 text-gray-800',
    icon: DollarSign,
    nextStatuses: [],
    description: 'Order has been refunded'
  },
};

export function OrderWorkflow({ order, onUpdate }: OrderWorkflowProps) {
  const { storeId } = useCurrentStore();
  const queryClient = useQueryClient();
  
  const [statusNote, setStatusNote] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [deliveryConfirmation, setDeliveryConfirmation] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: async ({ status, note }: { status: string; note?: string }) => {
      const { data } = await api.patch(`/api/orders/${order._id}/status`, {
        status,
        note,
        notifyCustomer: true,
      }, {
        headers: { 'Store-ID': storeId }
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', order._id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setStatusNote('');
      onUpdate?.();
    },
  });

  // Fulfillment update mutation
  const fulfillmentMutation = useMutation({
    mutationFn: async ({ type, metadata }: { type: string; metadata?: any }) => {
      const { data } = await api.patch(`/api/orders/${order._id}/fulfillment`, {
        type,
        status: true,
        metadata,
      }, {
        headers: { 'Store-ID': storeId }
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', order._id] });
      setTrackingNumber('');
      setDeliveryConfirmation('');
      onUpdate?.();
    },
  });

  // Cancel order mutation
  const cancelMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) => {
      const { data } = await api.post(`/api/orders/${order._id}/cancel`, {
        reason,
      }, {
        headers: { 'Store-ID': storeId }
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', order._id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setCancelReason('');
      onUpdate?.();
    },
  });

  const currentStatus = statusConfig[order.status as keyof typeof statusConfig];
  const StatusIcon = currentStatus?.icon || Clock;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: order.pricing.currency || 'NGN',
    }).format(amount);
  };

  const canTransitionTo = (status: string) => {
    return currentStatus?.nextStatuses.includes(status);
  };

  const handleStatusUpdate = (newStatus: string, note?: string) => {
    statusMutation.mutate({ status: newStatus, note });
  };

  const handleShipOrder = () => {
    if (!trackingNumber.trim()) return;
    
    // Update fulfillment and status
    fulfillmentMutation.mutate({
      type: 'shipped',
      metadata: {
        trackingNumber: trackingNumber.trim(),
        carrier: 'Standard Shipping',
      },
    });
  };

  const handleDeliverOrder = () => {
    fulfillmentMutation.mutate({
      type: 'delivered',
      metadata: {
        confirmedBy: deliveryConfirmation.trim() || 'Customer',
      },
    });
  };

  const handleCancelOrder = () => {
    if (!cancelReason.trim()) return;
    cancelMutation.mutate({ reason: cancelReason.trim() });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <StatusIcon className="h-5 w-5" />
          Order Workflow
        </CardTitle>
        <CardDescription>
          Manage order status and fulfillment process
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            <StatusIcon className="h-6 w-6" />
            <div>
              <Badge className={currentStatus?.color}>
                {currentStatus?.label}
              </Badge>
              <p className="text-sm text-muted-foreground mt-1">
                {currentStatus?.description}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="font-medium">{formatCurrency(order.pricing.total)}</div>
            <div className="text-sm text-muted-foreground">
              {order.items.length} item{order.items.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Quick Actions based on current status */}
        <div className="space-y-4">
          {order.status === 'pending' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                onClick={() => handleStatusUpdate('confirmed', 'Order confirmed by admin')}
                disabled={statusMutation.isPending}
                className="w-full"
              >
                {statusMutation.isPending ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Confirm Order
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <X className="h-4 w-4 mr-2" />
                    Cancel Order
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel Order</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. Please provide a reason for cancellation.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <Label>Cancellation Reason</Label>
                    <Textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Please provide a reason for cancelling this order..."
                      rows={3}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancelOrder}
                      disabled={!cancelReason.trim() || cancelMutation.isPending}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {cancelMutation.isPending ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        'Cancel Order'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {order.status === 'confirmed' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                onClick={() => handleStatusUpdate('processing', 'Order moved to processing')}
                disabled={statusMutation.isPending}
                className="w-full"
              >
                {statusMutation.isPending ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Package className="h-4 w-4 mr-2" />
                )}
                Start Processing
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <X className="h-4 w-4 mr-2" />
                    Cancel Order
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel Order</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. Please provide a reason for cancellation.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <Label>Cancellation Reason</Label>
                    <Textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Please provide a reason for cancelling this order..."
                      rows={3}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancelOrder}
                      disabled={!cancelReason.trim() || cancelMutation.isPending}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {cancelMutation.isPending ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        'Cancel Order'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {order.status === 'processing' && !order.fulfillment.shipped.status && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number"
                  />
                </div>
                <Button
                  onClick={handleShipOrder}
                  disabled={!trackingNumber.trim() || fulfillmentMutation.isPending}
                  className="w-full"
                >
                  {fulfillmentMutation.isPending ? (
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Truck className="h-4 w-4 mr-2" />
                  )}
                  Mark as Shipped
                </Button>
                <Button
                  variant="outline"
                  onClick={() => fulfillmentMutation.mutate({ type: 'packaged' })}
                  disabled={fulfillmentMutation.isPending}
                  className="w-full"
                >
                  Mark as Packaged
                </Button>
              </div>
            </div>
          )}

          {order.status === 'shipped' && !order.fulfillment.delivered.status && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                <div className="space-y-2">
                  <Label>Delivery Confirmation (Optional)</Label>
                  <Input
                    value={deliveryConfirmation}
                    onChange={(e) => setDeliveryConfirmation(e.target.value)}
                    placeholder="Customer name or signature"
                  />
                </div>
                <Button
                  onClick={handleDeliverOrder}
                  disabled={fulfillmentMutation.isPending}
                  className="w-full"
                >
                  {fulfillmentMutation.isPending ? (
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Mark as Delivered
                </Button>
              </div>

              {order.shipping.trackingNumber && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Tracking: {order.shipping.trackingNumber}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Status Update */}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                <ArrowRight className="h-4 w-4 mr-2" />
                Update Status
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update Order Status</DialogTitle>
                <DialogDescription>
                  Manually update the order status with an optional note.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Available Status Updates</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {Object.entries(statusConfig)
                      .filter(([status]) => canTransitionTo(status))
                      .map(([status, config]) => {
                        const Icon = config.icon;
                        return (
                          <Button
                            key={status}
                            variant="outline"
                            className="justify-start h-auto p-3"
                            onClick={() => {
                              handleStatusUpdate(status, statusNote || `Status updated to ${config.label}`);
                            }}
                            disabled={statusMutation.isPending}
                          >
                            <Icon className="h-4 w-4 mr-3" />
                            <div className="text-left">
                              <div className="font-medium">{config.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {config.description}
                              </div>
                            </div>
                          </Button>
                        );
                      })}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Status Note (Optional)</Label>
                  <Textarea
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder="Add a note about this status change..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setStatusNote('')}>
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

      

        {/* Warnings or Information */}
        {order.status === 'pending' && (
          <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Action Required</p>
              <p className="text-xs text-yellow-700">
                This order is pending confirmation. Please review and confirm or cancel.
              </p>
            </div>
          </div>
        )}

        {order.payment.status === 'pending' && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Payment Pending</p>
              <p className="text-xs text-amber-700">
                Payment for this order has not been confirmed yet.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
